// src/pages/Accounting/Reconcile/History/HistoryTable.tsx

import type React from "react";
import { useRef } from "react";
import { FileText, MoreHorizontal } from "lucide-react";
import { PALETTE } from "../constants";
import type { Currency } from "../types";
import { formatCurrency, formatDateLong } from "../utils";
import type { ReconciliationHistoryEntry } from "./historyTypes";

interface HistoryTableProps {
  entries: ReconciliationHistoryEntry[];
  currency: Currency;
  onViewStatement: (entry: ReconciliationHistoryEntry) => void;
  onOpenRowMenu: (entryId: string, anchor: HTMLButtonElement) => void;
}

export function HistoryTable({
  entries,
  currency,
  onViewStatement,
  onOpenRowMenu,
}: HistoryTableProps) {
  const menuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  if (entries.length === 0) {
    return (
      <div style={emptyStyle}>
        No past reconciliations for this account in the selected period.
      </div>
    );
  }

  const setMenuRef = (id: string) => (el: HTMLButtonElement | null) => {
    if (el) menuButtonRefs.current.set(id, el);
    else menuButtonRefs.current.delete(id);
  };

  return (
    <div style={wrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr style={headRowStyle}>
            <th style={thStyle}>Statement ending date</th>
            <th style={thStyle}>Reconciled on</th>
            <th style={thNumStyle}>Ending balance</th>
            <th style={thNumStyle}>Changes</th>
            <th style={thNumStyle}>Auto adjustment</th>
            <th style={thStyle}>Statements</th>
            <th style={thNumStyle}>Action</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} style={rowStyle}>
              <td style={{ ...tdStyle, fontWeight: 500, color: PALETTE.ink }}>
                {formatDateLong(entry.statementEndingDate)}
              </td>
              <td style={tdStyle}>
                {formatDateLong(entry.reconciledOn)}
              </td>
              <td style={{ ...tdNumStyle, color: PALETTE.ink, fontWeight: 600 }}>
                {formatCurrency(entry.endingBalance, currency)}
              </td>
              <td style={{ ...tdNumStyle, color: entry.changes === 0 ? PALETTE.ink4 : PALETTE.ink }}>
                {entry.changes}
              </td>
              <td style={{ ...tdNumStyle, color: entry.autoAdjustment === null ? PALETTE.ink4 : PALETTE.ink }}>
                {entry.autoAdjustment === null ? "\u2014" : formatCurrency(entry.autoAdjustment, currency)}
              </td>
              <td style={tdStyle}>
                {entry.statementAttached ? (
                  <button
                    type="button"
                    onClick={() => onViewStatement(entry)}
                    style={statementLinkStyle}
                  >
                    <FileText size={13} />
                    View statement
                  </button>
                ) : (
                  <span style={{ color: PALETTE.ink4, fontSize: 12.5 }}>No statement</span>
                )}
              </td>
              <td style={{ ...tdNumStyle, padding: "10px 12px" }}>
                <button
                  ref={setMenuRef(entry.id)}
                  type="button"
                  onClick={() => {
                    const anchor = menuButtonRefs.current.get(entry.id);
                    if (anchor) onOpenRowMenu(entry.id, anchor);
                  }}
                  style={menuBtnStyle}
                  aria-label="Row actions"
                >
                  <MoreHorizontal size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  overflow: "hidden",
  background: "#fff",
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};
const headRowStyle: React.CSSProperties = {
  background: PALETTE.bgFade,
  borderBottom: `1px solid ${PALETTE.line}`,
};
const thStyle: React.CSSProperties = {
  padding: "12px 16px",
  textAlign: "left",
  color: PALETTE.ink3,
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};
const thNumStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: "right",
};
const rowStyle: React.CSSProperties = {
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  background: "#fff",
};
const tdStyle: React.CSSProperties = {
  padding: "14px 16px",
  color: PALETTE.ink2,
  verticalAlign: "middle",
};
const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};
const statementLinkStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.tealDark,
  fontWeight: 600,
  fontSize: 12.5,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: 0,
  fontFamily: "inherit",
};
const menuBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  cursor: "pointer",
  padding: 6,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
};
const emptyStyle: React.CSSProperties = {
  padding: "48px 24px",
  textAlign: "center",
  color: PALETTE.ink3,
  fontSize: 13.5,
  fontWeight: 500,
  background: "#fff",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
};